<template>
  <div class="flex h-full">
    <!-- Main staging area -->
    <div class="flex-1 overflow-y-auto">
      <!-- Summary banner -->
      <div class="bg-neutral-100 dark:bg-neutral-800 border-b border-neutral-300 dark:border-neutral-700 px-4 py-3 flex items-center justify-between">
        <div class="flex items-center gap-6 text-sm">
          <select
            v-model="selectedSourceId"
            class="rounded border-0 bg-neutral-200 dark:bg-neutral-700 py-1 px-2 text-sm text-neutral-700 dark:text-white"
          >
            <option :value="null">
              All Sources
            </option>
            <option
              v-for="source in sources"
              :key="source.id"
              :value="source.id"
            >
              {{ app.queries.getSourceName(source) }}
            </option>
          </select>
          <div>
            <span class="text-neutral-500">Staged:</span>
            <span class="text-neutral-800 dark:text-white ml-1">{{ stagedCount }}</span>
          </div>
          <div>
            <span class="text-neutral-500">Pending:</span>
            <span class="text-yellow-600 dark:text-yellow-500 ml-1">{{ pendingCount }}</span>
          </div>
          <div>
            <span class="text-neutral-500">Cleared:</span>
            <span class="text-green-600 dark:text-green-500 ml-1">{{ clearedCount }}</span>
          </div>
          <div>
            <span class="text-neutral-500">Ignored:</span>
            <span class="text-neutral-400 dark:text-neutral-600 ml-1">{{ ignoredCount }}</span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            class="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-500"
            @click="addTransaction"
          >
            + Add
          </button>
          <button
            class="px-3 py-1.5 text-sm rounded text-white transition-all"
            :class="copied ? 'bg-green-400' : 'bg-green-600 hover:bg-green-500'"
            @click="copyStaged"
          >
            {{ copied ? 'Copied!' : 'Copy Cleared' }}
          </button>
          <button
            class="px-3 py-1.5 text-sm rounded bg-red-600/80 text-white hover:bg-red-500"
            @click="clearStaged"
          >
            Clear
          </button>
        </div>
      </div>

      <!-- Staged transactions table -->
      <table class="w-full divide-y divide-neutral-200 dark:divide-neutral-800 text-sm">
        <thead>
          <tr class="text-left text-neutral-500">
            <th class="py-2 px-3 w-10" />
            <th class="py-2 px-3">
              Date
            </th>
            <th class="py-2 px-3">
              Payee
            </th>
            <th class="py-2 px-3">
              Postings
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-neutral-200 dark:divide-neutral-800">
          <tr
            v-for="transaction in stagedTransactions"
            :key="transaction.id"
            :class="transaction.status === 'ignored' ? 'text-neutral-400 dark:text-neutral-600 line-through opacity-50' : 'text-neutral-600 dark:text-neutral-400'"
            class="cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-800/50"
            @click="editTransaction(transaction)"
          >
            <td class="py-2 px-3">
              <button
                class="w-8 h-8 flex items-center justify-center rounded hover:bg-neutral-200 dark:hover:bg-neutral-700"
                @click.stop="toggleStatus(transaction)"
              >
                <span
                  v-if="transaction.status === 'ignored'"
                  class="text-neutral-500"
                >✕</span>
                <span
                  v-else-if="transaction.status === 'cleared'"
                  class="text-green-500"
                >*</span>
                <span
                  v-else
                  class="text-yellow-600 dark:text-yellow-500"
                >!</span>
              </button>
            </td>
            <td class="py-2 px-3">
              {{ floatToDateString(transaction.date) }}
            </td>
            <td class="py-2 px-3 truncate max-w-48">
              {{ transaction.payee }}
            </td>
            <td class="py-2 px-3">
              <div
                v-for="(posting, idx) in transaction.postings"
                :key="idx"
                class="flex justify-between"
              >
                <span
                  class="truncate max-w-48 mr-2"
                  :title="posting.account"
                >{{ posting.account }}</span>
                <span class="whitespace-nowrap tabular-nums">{{ formatAmount(posting.amount, posting.currency) }}</span>
              </div>
              <div v-if="!transaction.postings?.length">
                —
              </div>
            </td>
          </tr>
          <tr v-if="!stagedTransactions.length">
            <td
              colspan="4"
              class="py-8 text-center text-neutral-500"
            >
              No staged transactions. Sync a bank source to import transactions.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Rules sidebar -->
    <div class="w-[35%] border-l border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 flex flex-col">
      <!-- Rules section - 50% height -->
      <div class="h-1/2 flex flex-col">
        <div class="px-4 py-3 bg-neutral-200 dark:bg-neutral-800 flex items-center justify-between">
          <h3 class="text-neutral-700 dark:text-white font-semibold text-sm uppercase tracking-wide">
            Rules
          </h3>
          <button
            class="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
            @click="$refs.rulesEditor.addRule()"
          >
            + Add
          </button>
        </div>
        <div class="flex-1 overflow-y-auto">
          <RulesEditor
            ref="rulesEditor"
            :app="app"
            from="staging"
          />
        </div>
      </div>

      <!-- Balances section - 50% height -->
      <div class="h-1/2 flex flex-col border-t border-neutral-200 dark:border-neutral-800">
        <div class="px-4 py-3 bg-neutral-200 dark:bg-neutral-800 flex items-center justify-between">
          <h3 class="text-neutral-700 dark:text-white font-semibold text-sm uppercase tracking-wide">
            Balances
          </h3>
          <label class="flex items-center gap-1.5 text-xs text-neutral-500 cursor-pointer">
            <input
              v-model="includeClearedInBalance"
              type="checkbox"
              class="rounded border-neutral-400 dark:border-neutral-600"
            >
            Cleared
          </label>
        </div>
        <div
          v-if="sourceBalanceTargets.length"
          class="px-3 py-2 border-b border-neutral-200 dark:border-neutral-700 space-y-1"
        >
          <div
            v-for="target in sourceBalanceTargets"
            :key="target.sourceId"
            class="flex items-center justify-between text-sm"
          >
            <span class="text-neutral-500 truncate">{{ target.sourceName }}</span>
            <span class="flex items-center gap-2">
              <span
                class="tabular-nums"
                :class="target.matches ? 'text-green-500' : 'text-neutral-400'"
              >{{ formatAmount(target.computed, target.currency) }}</span>
              <span class="text-neutral-500">/</span>
              <span class="tabular-nums text-neutral-400">{{ formatAmount(target.reported, target.currency) }}</span>
              <span
                v-if="target.matches"
                class="text-green-500"
              >✓</span>
            </span>
          </div>
        </div>
        <div class="flex-1 overflow-y-auto p-2">
          <pre
            v-if="balanceLines.length"
            class="text-sm text-neutral-400"
          ><span
            v-for="(line, idx) in balanceLines"
            :key="idx"
          >{{ pad(formatAmount(line.sum, line.currency), 16) }} {{ pad('', line.indent) }}|- <a
            href="javascript:;"
            class="hover:underline hover:text-indigo-400"
            @click="selectAccount(line.account)"
          >{{ lastSplat(line.account) }}</a>
</span></pre>
          <div
            v-else
            class="text-neutral-500 text-center py-4 text-sm"
          >
            No balances
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatAmount, floatToDateString, pad, postingsForAccount, postingsMatchingPattern, sumPostingsForCurrency } from '@/parse-and-play/utils'
import PlainTextExporter from '@/adapters/exporters/plain-text.js'
import RulesEditor from '@/app/components/rules-editor/component.vue'

export default {
  components: {
    RulesEditor
  },

  props: ['app', 'sheet', 'transactions'],

  data () {
    return {
      copied: false,
      selectedSourceId: null,
      includeClearedInBalance: true
    }
  },

  computed: {
    sources () {
      return this.app.queries.findAllSources(this.app.identity.id)
        .filter(s => s.type === 'BankAccount')
    },

    allStagedTransactions () {
      return this.app.queries.findAllStagedTransactions(this.app.identity.id)
        .sort((a, b) => a.date - b.date)
    },

    stagedTransactions () {
      if (!this.selectedSourceId) return this.allStagedTransactions
      return this.allStagedTransactions.filter(t => t.sourceId === this.selectedSourceId)
    },

    stagedCount () {
      return this.stagedTransactions.length
    },

    pendingCount () {
      return this.stagedTransactions.filter(t => !t.status || t.status === 'pending').length
    },

    clearedCount () {
      return this.stagedTransactions.filter(t => t.status === 'cleared').length
    },

    ignoredCount () {
      return this.stagedTransactions.filter(t => t.status === 'ignored').length
    },

    balancePostings () {
      const allTransactions = this.app.queries.findAllTransactions(this.app.identity.id)
        .filter(t => !t.isStaged || (this.includeClearedInBalance && t.status === 'cleared'))

      return allTransactions.reduce((acc, t) => {
        const assetLiabilityPostings = (t.postings || []).filter(p =>
          p.account?.startsWith('Assets:') || p.account?.startsWith('Liabilities:')
        )
        return acc.concat(assetLiabilityPostings)
      }, [])
    },

    balanceAccounts () {
      const accs = [...new Set(this.balancePostings.map(p => p.account))].sort()
      const explodedAccounts = []

      for (let i = 0; i < accs.length; i++) {
        const splat = accs[i].split(':')
        for (let n = 0; n < splat.length; n++) {
          const fullAccount = splat.slice(0, n + 1).join(':')
          if (explodedAccounts.indexOf(fullAccount) === -1) {
            explodedAccounts.push(fullAccount)
          }
        }
      }

      return explodedAccounts
    },

    balanceCurrencies () {
      return [...new Set(this.balancePostings.map(p => p.currency))]
    },

    balanceLines () {
      const lines = []

      for (const currency of this.balanceCurrencies) {
        for (const account of this.balanceAccounts) {
          const postings = postingsForAccount(this.balancePostings, account)
          const sum = sumPostingsForCurrency(postings, currency)
          const anyNestedAccountsWithAmounts = this.balanceAccounts
            .filter(a => a.indexOf(`${account}:`) === 0)
            .map(a => postingsForAccount(this.balancePostings, a))
            .filter(p => Math.abs(sumPostingsForCurrency(p, currency)).toLocaleString() !== '0')
            .length

          if (Math.abs(sum).toLocaleString() !== '0' || anyNestedAccountsWithAmounts) {
            const indent = (account.split(':').length - 1) * 1
            lines.push({ sum, currency, indent, account })
          }
        }
      }

      return lines
    },

    sourceBalanceTargets () {
      return this.sources
        .filter(s => s.config?.reportedBalance !== undefined && s.config?.elidedAccount)
        .map(source => {
          const account = source.config.elidedAccount
          const pattern = source.config.balanceAccountPattern
          const postings = pattern
            ? postingsMatchingPattern(this.balancePostings, pattern)
            : postingsForAccount(this.balancePostings, account)
          // A source has no currency of its own -- take it from its
          // postings rather than assuming CAD, or a USD account's
          // computed balance comes back 0 and its checkmark is meaningless.
          const currency = [...new Set(postings.map(p => p.currency).filter(Boolean))][0] || 'CAD'
          const computed = sumPostingsForCurrency(postings, currency)
          const reported = parseFloat(source.config.reportedBalance)
          const matches = Math.abs(computed - reported) < 0.01

          return {
            sourceId: source.id,
            sourceName: this.app.queries.getSourceName(source),
            account,
            currency,
            computed,
            reported,
            matches
          }
        })
    }
  },

  mounted () {
    this.app.commands.recategorizeStagedTransactions(this.app.identity)
  },

  methods: {
    formatAmount,
    floatToDateString,
    pad,

    lastSplat (account) {
      const splat = account.split(':')
      return splat[splat.length - 1]
    },

    selectAccount (account) {
      const transactions = this.stagedTransactions.filter(t =>
        (t.postings || []).some(p => p.account?.startsWith(account))
      )
      this.app.zoom(account, transactions)
    },

    toggleStatus (transaction) {
      const cycle = ['pending', 'cleared', 'ignored']
      const currentIndex = cycle.indexOf(transaction.status || 'pending')
      const nextStatus = cycle[(currentIndex + 1) % cycle.length]

      this.app.commands.saveStagedTransactionForIdentity(
        this.app.identity,
        transaction,
        { status: nextStatus }
      )
      transaction.status = nextStatus
    },

    addTransaction () {
      this.$router.push({
        name: 'stagedNew',
        params: { identity_id: this.app.identity.id }
      })
    },

    editTransaction (transaction) {
      this.$router.push({
        name: 'stagedEdit',
        params: { identity_id: this.app.identity.id, staged_id: transaction.id }
      })
    },

    copyStaged () {
      const cleared = this.stagedTransactions
        .filter(t => t.status === 'cleared')
        .sort((a, b) => a.date - b.date)
      const skipped = this.stagedTransactions
        .filter(t => t.status !== 'cleared')
        .sort((a, b) => a.date - b.date)

      const exporter = new PlainTextExporter([], cleared, [])
      let text = exporter.toString()

      if (skipped.length) {
        text += '\n; --- Skipped transactions (for hash tracking) ---\n'
        skipped.forEach(t => {
          const amount = t.postings?.[0]?.amount
          const amountStr = amount !== undefined ? ` ${formatAmount(amount, t.postings?.[0]?.currency)}` : ''
          text += `; ${floatToDateString(t.date)} ${t.payee}${amountStr} ${t.hash || ''}\n`
        })
      }

      navigator.clipboard.writeText(text)
      this.copied = true
      setTimeout(() => { this.copied = false }, 1500)
    },

    clearStaged () {
      const count = this.stagedTransactions.length
      if (confirm(`Clear all ${count} staged transactions?`)) {
        this.app.commands.clearStagedTransactionsForIdentity(this.app.identity)
      }
    }
  }
}
</script>
